import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import {
  GridRow,
  type GridRowModel,
  type GridRowProps,
} from "@mui/x-data-grid";
import type { MouseEvent } from "react";
import { useState } from "react";

import { PublicSvgIcon } from "~/components/public_svg_icon";

type LinkableRow = GridRowModel;

interface LinkableGridRowMenuProps {
  // Cursor coordinates to anchor the menu at, or `null` when it is closed.
  position: { top: number; left: number } | null;
  onClose: () => void;
  onCopyLink: () => void;
  onOpenInNewTab: () => void;
}

function LinkableGridRowMenu({
  position,
  onClose,
  onCopyLink,
  onOpenInNewTab,
}: LinkableGridRowMenuProps) {
  const runAndClose = (action: () => void) => () => {
    action();
    onClose();
  };

  return (
    <Menu
      open={position !== null}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={position ?? undefined}
      slotProps={{
        // A right click outside the menu closes it instead of opening the
        // browser's context menu on the backdrop.
        root: {
          onContextMenu: (event: MouseEvent<HTMLElement>) => {
            event.preventDefault();
            onClose();
          },
        },
      }}
    >
      <MenuItem onClick={runAndClose(onOpenInNewTab)}>
        <ListItemIcon>
          <PublicSvgIcon name="open_in_new" />
        </ListItemIcon>
        <ListItemText>Open in new tab</ListItemText>
      </MenuItem>
      <MenuItem onClick={runAndClose(onCopyLink)}>
        <ListItemIcon>
          <PublicSvgIcon name="content_copy" />
        </ListItemIcon>
        <ListItemText>Copy link</ListItemText>
      </MenuItem>
    </Menu>
  );
}

interface LinkableGridRowProps<T = LinkableRow> extends GridRowProps {
  getUrl?: (row: T) => string;
  // Same-tab navigation is delegated to the caller so this component stays
  // free of the router: the caller wraps its own `navigate({ to })`.
  onNavigate?: (url: string) => void;
}

// 0 = left -> open in same tab
// 0 + ctrl/meta -> open in new tab
// 1 = middle -> open in new tab
// 2 = right -> row actions menu (copy link / open in new tab).
export function LinkableGridRow(props: LinkableGridRowProps<LinkableRow>) {
  const { getUrl, onNavigate, ...muiProps } = props;
  const row = muiProps.row as LinkableRow | undefined;
  const url = row && getUrl ? getUrl(row) : undefined;

  const [contextMenu, setContextMenu] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const closeContextMenu = () => setContextMenu(null);
  const openInNewTab = () => {
    if (url) {
      window.open(url, "_blank");
    }
  };
  const copyLink = () => {
    if (url) {
      void navigator.clipboard.writeText(window.location.origin + url);
    }
  };

  return (
    <>
      <GridRow
        {...muiProps}
        onClick={(event) => {
          if (event.button === 0 && (event.ctrlKey || event.metaKey)) {
            openInNewTab();
          } else if (event.button === 0 && onNavigate && url) {
            // Don't navigate if the user is selecting text in the row (e.g.
            // double-click to select a word, or drag to select a range).
            if (!window.getSelection()?.isCollapsed) {
              return;
            }
            onNavigate(url);
          }
        }}
        onAuxClick={(event) => {
          if (event.button === 1) {
            event.preventDefault();
            openInNewTab();
          }
        }}
        onContextMenu={(event) => {
          if (!url) {
            return;
          }
          event.preventDefault();
          setContextMenu({ top: event.clientY, left: event.clientX });
        }}
      />
      <LinkableGridRowMenu
        position={contextMenu}
        onClose={closeContextMenu}
        onCopyLink={copyLink}
        onOpenInNewTab={openInNewTab}
      />
    </>
  );
}
